import { ServiceContext, Survey, SurveyWrapper } from '@confer/lib'
import { useContext, useEffect, useState } from 'react'

import LoadingSplash from '../layout/LoadingSplash'
import SurveyLayout from '../layout/SurveyLayout'
// import type { SurveyReduxState } from '@confer/lib'

interface SurveyPageProps {
  survey: Survey
}

const SurveyPage = ({ survey }: SurveyPageProps) => {
  const { databaseService, userService } = useContext(ServiceContext)

  // Ensure we have a Firebase user (anonymous or logged-in) before anything else
  useEffect(() => {
    const sub = userService.observeUser().subscribe(async (u) => {
      if (u.initialised && !u.user) {
        await userService.createAnonymousUser()
      }
    })
    return () => sub.unsubscribe()
  }, [userService])

  const savedSubmissionId = sessionStorage.getItem('submissionId')

  // Local state for the active submission id we will feed to SurveyWrapper
  const [submissionId, setSubmissionId] = useState<string | null>(savedSubmissionId ?? null)

  const [initialState, setInitialState] = useState<any>(undefined)

  // Once the user is initialised and we have no prior id, create a fresh submission
  useEffect(() => {
    if (submissionId) return // already have one

    const subscription = userService.observeUser().subscribe(async (u) => {
      if (u.initialised && u.user) {
        try {
          const { submissionId: newId } = await databaseService.createSubmission(
            survey.settings.surveyName,
            u.user,
          )
          setSubmissionId(newId)
          sessionStorage.setItem('submissionId', newId)
        } catch (e) {
          console.error('Failed to create submission', e)
        }
        subscription.unsubscribe()
      }
    })

    return () => subscription.unsubscribe()
  }, [submissionId, databaseService, userService, survey.settings.surveyName])

  // Load existing state when we have an id
  useEffect(() => {
    const loadOrCreate = async () => {
      if (!submissionId) return
      try {
        const state = await databaseService.loadSubmissionState(
          survey.settings.surveyName,
          submissionId,
        )
        if (state) {
          setInitialState(state)
        } else {
          // Submission not found – create a new one
          const { submissionId: newId } = await databaseService.createSubmission(
            survey.settings.surveyName,
            userService.getUser()!,
          )
          sessionStorage.setItem('submissionId', newId)
          setSubmissionId(newId)
          setInitialState(undefined) // will trigger effect again
        }
      } catch (e) {
        console.error('Failed to load submission state', e)
        // attempt to create new submission
        try {
          const { submissionId: newId } = await databaseService.createSubmission(
            survey.settings.surveyName,
            userService.getUser()!,
          )
          sessionStorage.setItem('submissionId', newId)
          setSubmissionId(newId)
          setInitialState(undefined)
        } catch (err) {
          console.error('Failed to create fallback submission', err)
          setInitialState(null)
        }
      }
    }
    loadOrCreate()
  }, [submissionId, databaseService, survey.settings.surveyName, userService])

  if (!submissionId || initialState === undefined) {
    return <LoadingSplash backgroundColor={survey.settings.primaryColour} />
  }

  return (
    <SurveyWrapper
      survey={survey}
      databaseService={databaseService}
      userService={userService}
      submissionId={submissionId}
      initialState={initialState}
    >
      {survey.settings.font && <style>{`@import url(${survey.settings.font.source})`}</style>}
      <SurveyLayout />
    </SurveyWrapper>
  )
}

export default SurveyPage
